import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { sendInviteEmail, resetInviteStatus, fetchUserReferral, clearReferralError } from '../features/invite/invite.slice';
import '../scss/invite-friend-modal.component.scss';

interface InviteFriendModalProps {
    onClose: () => void;
}

const InviteFriendModal: React.FC<InviteFriendModalProps> = ({ onClose }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const loading = useAppSelector((state) => state.invite?.loading);
    const error = useAppSelector((state) => state.invite?.error);
    const success = useAppSelector((state) => state.invite?.success);
    const language = new URLSearchParams(window.location.search).get('lang') || 'sv-se';

    // Referral data selectors
    const referralData = useAppSelector((state) => state.invite?.referralData);
    const referralLoading = useAppSelector((state) => state.invite?.referralLoading);
    const referralError = useAppSelector((state) => state.invite?.referralError);

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        dispatch(resetInviteStatus());
        // Fetch referral data when modal opens
        dispatch(fetchUserReferral(language));
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            setEmail('');
            toast.success((t('INVITE_SENT_SUCCESSFULLY') || 'Invitation sent successfully!') as string);
            // Reset the success state immediately after showing toast
            dispatch(resetInviteStatus());
            setTimeout(() => {
                onClose();
            }, 2000);
        }
    }, [success, onClose, t, dispatch]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleShareInvite = async () => {
        setEmailError('');
        if (!email.trim()) {
            setEmailError(t('EMAIL_REQUIRED') as string);
            return;
        }
        if (!validateEmail(email)) {
            setEmailError(t('INVALID_EMAIL_FORMAT') as string);
            return;
        }
        try {
            const result = await dispatch(sendInviteEmail({
                email: email.trim(),
                lang:  language || 'sv-se'
            }));

            if (sendInviteEmail.fulfilled.match(result)) {
            } else {
                toast.error((t('INVITE_SEND_ERROR') || 'Failed to send invitation') as string);
            }   
        } catch (error) {
            console.error('Failed to send invite:', error);
            toast.error((t('INVITE_SEND_ERROR') || 'Failed to send invitation') as string);
        }
    };

    const handleSocialShare = (platform: string) => {
        const baseUrl = origin; // e.g. https://www.invozio.com
        const referralPath = referralData?.response || '';

        // Final public share URL (NO localhost)
        const shareUrl = `${baseUrl}${referralPath}`;

        const shareText =
            t('INVITE_FRIEND_SHARE_TEXT') ||
            'Join me on Invozio and start invoicing like a pro!';

        const textWithLink = shareUrl;

        if (referralError) {
            toast.error(
                (t('REFERRAL_LINK_ERROR') ||
                    'Failed to get referral link. Using default link.') as string
            );
        }

        switch (platform) {
            case 'email':
                navigator.clipboard
                    .writeText(textWithLink)
                    .then(() =>
                        toast.success(
                            (t('REFERRAL_LINK_COPIED') ||
                                'Referral link copied to clipboard!') as string
                        )
                    )
                    .catch(() =>
                        toast.error(
                            (t('COPY_FAILED') ||
                                'Failed to copy link to clipboard') as string
                        )
                    );
                break;

            case 'facebook':
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        shareUrl
                    )}&quote=${encodeURIComponent(shareText)}`,
                    '_blank'
                );
                break;

            case 'linkedin':
                window.open(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        shareUrl
                    )}`,
                    '_blank'
                );
                break;

            case 'twitter':
                window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        textWithLink
                    )}`,
                    '_blank'
                );
                break;

            case 'whatsapp':
                window.open(
                    `https://api.whatsapp.com/send?text=${encodeURIComponent(
                        textWithLink
                    )}`,
                    '_blank'
                );
                break;

            default:
                navigator.clipboard.writeText(textWithLink).then(() => {
                    window.open('https://www.instagram.com/', '_blank');
                    toast.success(
                        (t('INSTAGRAM_CONTENT_COPIED') ||
                            'Content copied! Paste it on Instagram.') as string
                    );
                });
        }
    };

    return (
        <div className="invite-friend-backdrop" onClick={handleBackdropClick}>
            <div className="invite-friend-modal" role="dialog" aria-labelledby="invite-friend-title" aria-modal="true">
                <div className="invite-friend-header">
                    <h2 id="invite-friend-title" className="invite-friend-title">
                        {t('INVITE_FRIEND_MODAL_TITLE')}
                    </h2>
                    <button
                        className="close-button"
                        onClick={onClose}
                        aria-label={t('CLOSE')}
                        type="button"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                <div className="invite-friend-body">
                    <div className="invite-friend-content">
                        <h3 className="invite-friend-headline">
                            {t('INVITE_FRIEND_MODAL_HEADLINE')}
                        </h3>
                        <p className="invite-friend-description">
                            {t('INVITE_FRIEND_MODAL_DESCRIPTION')}
                        </p>
                    </div>

                    <div className="invite-input-section">
                        <label htmlFor="invite-email" className="input-label">
                            {t('INVITE_FRIEND_EMAIL_LABEL') || 'Share an invite to your friend'}
                        </label>
                        <div className="input-group">
                            <input
                                id="invite-email"
                                type="email"
                                className={`email-input ${emailError ? 'error' : ''}`}
                                placeholder={t('INVITE_EMAIL_PLACEHOLDER') || "Enter your friend's email"}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    validateEmail(e.target.value);
                                    if (emailError) setEmailError('');
                                }}
                                disabled={loading}
                            />
                            <button
                                className={`invite-button  ${loading ? 'loading' : ''} ${success ? 'success' : ''}`}
                                onClick={handleShareInvite}
                                disabled={!email.trim() || loading || success}
                                type="button"
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        {t('SENDING_INVITE') || 'Sending...'}
                                    </>
                                ) : success ? (
                                    <>
                                        <span className="success-icon">✓</span>
                                        {t('INVITE_SENT_SUCCESSFULLY') || 'Invite Sent!'}
                                    </>
                                ) : (
                                    t('INVITE_FRIEND_SHARE_BUTTON') || 'Invite'
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="social-share-section">
                        <p className="share-text-invite gap-3">
                            <span className='text-danger'>{t(emailError)} </span> <br />
                            {t('SHARE_VIA_TEXT') || 'Or share via'}
                        </p>

                        <div className="social-icons-invite">
                            <button
                                className="social-icon"
                                type="button"
                                aria-label="Copy referral link"
                                onClick={() => handleSocialShare('email')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M4.7577 9.00034L3.69704 10.061C2.52547 11.2326 2.52547 13.1321 3.69704 14.3036C4.86861 15.4752 6.76811 15.4752 7.93968 14.3036L9.00034 13.243M9.00034 4.7577L10.061 3.69704C11.2326 2.52547 13.1321 2.52547 14.3036 3.69704C15.4752 4.86861 15.4752 6.76811 14.3036 7.93968L13.243 9.00034M6.87917 11.1218L11.1218 6.87916" stroke="black" stroke-width="2" stroke-linecap="round" />
                                </svg>
                            </button>
                            <button
                                className="social-icon"
                                type="button"
                                aria-label="Share via Facebook"
                                onClick={() => handleSocialShare('facebook')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M18 9C18 4.02943 13.9706 0 9 0C4.02943 0 0 4.02943 0 9C0 13.4921 3.29115 17.2155 7.59375 17.8907V11.6016H5.30859V9H7.59375V7.01719C7.59375 4.76156 8.93742 3.51562 10.9932 3.51562C11.9776 3.51562 13.0078 3.69141 13.0078 3.69141V5.90625H11.873C10.755 5.90625 10.4062 6.60006 10.4062 7.3125V9H12.9023L12.5033 11.6016H10.4062V17.8907C14.7088 17.2155 18 13.4921 18 9Z" fill="#1877F2" />
                                    <path d="M12.5033 11.6016L12.9023 9H10.4062V7.3125C10.4062 6.60076 10.755 5.90625 11.873 5.90625H13.0078V3.69141C13.0078 3.69141 11.9779 3.51562 10.9932 3.51562C8.93742 3.51562 7.59375 4.76156 7.59375 7.01719V9H5.30859V11.6016H7.59375V17.8907C8.52558 18.0364 9.47442 18.0364 10.4062 17.8907V11.6016H12.5033Z" fill="white" />
                                </svg>
                            </button>
                            {/* <button
                                className="social-icon"
                                type="button"
                                aria-label="Share via Instagram"
                                onClick={() => handleSocialShare('instagram')}
                            >

                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M9 1.621C11.403 1.621 11.688 1.631 12.637 1.678C13.514 1.722 13.99 1.86 14.308 1.977C14.73 2.135 15.027 2.324 15.338 2.635C15.649 2.946 15.838 3.243 15.996 3.665C16.113 3.983 16.251 4.459 16.295 5.336C16.342 6.285 16.352 6.57 16.352 8.973C16.352 11.376 16.342 11.661 16.295 12.61C16.251 13.487 16.113 13.963 15.996 14.281C15.838 14.703 15.649 15 15.338 15.311C15.027 15.622 14.73 15.811 14.308 15.969C13.99 16.086 13.514 16.224 12.637 16.268C11.688 16.315 11.403 16.325 9 16.325C6.597 16.325 6.312 16.315 5.363 16.268C4.486 16.224 4.01 16.086 3.692 15.969C3.27 15.811 2.973 15.622 2.662 15.311C2.351 15 2.162 14.703 2.004 14.281C1.887 13.963 1.749 13.487 1.705 12.61C1.658 11.661 1.648 11.376 1.648 8.973C1.648 6.57 1.658 6.285 1.705 5.336C1.749 4.459 1.887 3.983 2.004 3.665C2.162 3.243 2.351 2.946 2.662 2.635C2.973 2.324 3.27 2.135 3.692 1.977C4.01 1.86 4.486 1.722 5.363 1.678C6.312 1.631 6.597 1.621 9 1.621ZM9 0C6.556 0 6.249 0.011 5.289 0.059C4.331 0.107 3.677 0.252 3.105 0.473C2.513 0.702 2.011 1.016 1.511 1.516C1.011 2.016 0.697 2.518 0.468 3.11C0.247 3.682 0.102 4.336 0.054 5.294C0.006 6.254 -0.005 6.561 -0.005 9.005C-0.005 11.449 0.006 11.756 0.054 12.716C0.102 13.674 0.247 14.328 0.468 14.9C0.697 15.492 1.011 15.994 1.511 16.494C2.011 16.994 2.513 17.308 3.105 17.537C3.677 17.758 4.331 17.903 5.289 17.951C6.249 17.999 6.556 18.01 9 18.01C11.444 18.01 11.751 17.999 12.711 17.951C13.669 17.903 14.323 17.758 14.895 17.537C15.487 17.308 15.989 16.994 16.489 16.494C16.989 15.994 17.303 15.492 17.532 14.9C17.753 14.328 17.898 13.674 17.946 12.716C17.994 11.756 18.005 11.449 18.005 9.005C18.005 6.561 17.994 6.254 17.946 5.294C17.898 4.336 17.753 3.682 17.532 3.11C17.303 2.518 16.989 2.016 16.489 1.516C15.989 1.016 15.487 0.702 14.895 0.473C14.323 0.252 13.669 0.107 12.711 0.059C11.751 0.011 11.444 0 9 0Z" fill="url(#paint0_radial_instagram)" />
                                    <path d="M9 4.378C6.448 4.378 4.378 6.448 4.378 9C4.378 11.552 6.448 13.622 9 13.622C11.552 13.622 13.622 11.552 13.622 9C13.622 6.448 11.552 4.378 9 4.378ZM9 12.001C7.345 12.001 5.999 10.655 5.999 9C5.999 7.345 7.345 5.999 9 5.999C10.655 5.999 12.001 7.345 12.001 9C12.001 10.655 10.655 12.001 9 12.001Z" fill="url(#paint1_radial_instagram)" />
                                    <path d="M13.807 5.288C14.454 5.288 14.981 4.761 14.981 4.114C14.981 3.467 14.454 2.94 13.807 2.94C13.16 2.94 12.633 3.467 12.633 4.114C12.633 4.761 13.16 5.288 13.807 5.288Z" fill="url(#paint2_radial_instagram)" />
                                    <defs>
                                        <radialGradient id="paint0_radial_instagram" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(4.5 19.35) rotate(-90) scale(17.85 16.59)">
                                            <stop stop-color="#FFDD55" />
                                            <stop offset="0.1" stop-color="#FFDD55" />
                                            <stop offset="0.5" stop-color="#FF543E" />
                                            <stop offset="1" stop-color="#C837AB" />
                                        </radialGradient>
                                        <radialGradient id="paint1_radial_instagram" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(4.5 19.35) rotate(-90) scale(17.85 16.59)">
                                            <stop stop-color="#FFDD55" />
                                            <stop offset="0.1" stop-color="#FFDD55" />
                                            <stop offset="0.5" stop-color="#FF543E" />
                                            <stop offset="1" stop-color="#C837AB" />
                                        </radialGradient>
                                        <radialGradient id="paint2_radial_instagram" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(4.5 19.35) rotate(-90) scale(17.85 16.59)">
                                            <stop stop-color="#FFDD55" />
                                            <stop offset="0.1" stop-color="#FFDD55" />
                                            <stop offset="0.5" stop-color="#FF543E" />
                                            <stop offset="1" stop-color="#C837AB" />
                                        </radialGradient>
                                    </defs>
                                </svg>
                            </button> */}
                            <button
                                className="social-icon"
                                type="button"
                                aria-label="Share via LinkedIn"
                                onClick={() => handleSocialShare('linkedin')}
                            >
                                <svg
                                    width={18}
                                    height={18}
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0 1.28939C0 0.577688 0.595828 0 1.33031 0H16.6697C17.4045 0 18 0.577688 18 1.28939V16.7108C18 17.4227 17.4045 18 16.6697 18H1.33031C0.595898 18 0 17.4228 0 16.711V1.28918V1.28939Z"
                                        fill="#006699"
                                    />
                                    <path
                                        d="M5.46948 15.0628V6.95916H2.77595V15.0628H5.46977H5.46948ZM4.12328 5.85293C5.06238 5.85293 5.64702 5.23066 5.64702 4.45301C5.62945 3.65763 5.06238 3.05273 4.14114 3.05273C3.21927 3.05273 2.61719 3.65763 2.61719 4.45294C2.61719 5.23059 3.20162 5.85286 4.10563 5.85286H4.12307L4.12328 5.85293ZM6.96039 15.0628H9.65371V10.5379C9.65371 10.296 9.67129 10.0535 9.74245 9.88071C9.93707 9.39661 10.3803 8.89549 11.1245 8.89549C12.0989 8.89549 12.4889 9.63855 12.4889 10.728V15.0628H15.1822V10.4164C15.1822 7.92743 13.8535 6.76917 12.0815 6.76917C10.6286 6.76917 9.99051 7.58121 9.63599 8.13429H9.65392V6.95944H6.96053C6.99569 7.71966 6.96032 15.0631 6.96032 15.0631L6.96039 15.0628Z"
                                        fill="white"
                                    />
                                </svg>

                            </button>
                            <button
                                className="social-icon"
                                type="button"
                                aria-label="Share via Twitter"
                                onClick={() => handleSocialShare('twitter')}
                            >
                                <svg
                                    width={16}
                                    height={16}
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9.52373 6.77569L15.4811 0H14.0699L8.89493 5.88203L4.7648 0H0L6.24693 8.89552L0 16H1.4112L6.87253 9.78704L11.2352 16H16M1.92053 1.04127H4.08853L14.0688 15.0099H11.9003"
                                        fill="black"
                                    />
                                </svg>

                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteFriendModal;
